import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GroundworksTableComponent,
  GroundworksTableData,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
  StateMessageComponent,
} from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PaymentHistoryEntry, PaymentHistoryEntryKind, SwaggerException } from '../../sdk/sdk';
import { PaymentService } from '../../services/payment.service';

const KIND_LABELS: Record<PaymentHistoryEntryKind, string> = {
  [PaymentHistoryEntryKind.PaymentRequest]: 'Payment Request',
  [PaymentHistoryEntryKind.DirectCharge]: 'Direct Charge',
  [PaymentHistoryEntryKind.RecordedPayment]: 'Recorded Payment',
  // Account credits never reach this fallback — toTypeCell labels them "[ACR]" instead. The key
  // stays because the Record must cover the enum, and it keeps the map readable as a whole.
  [PaymentHistoryEntryKind.AccountCredit]: 'Account Credit',
};

// The wire contract deliberately sends the ACR method name unprefixed — the account-credit design
// (docs/superpowers/specs/2026-08-26-payment-history-account-credit-design.md) ruled that `kind`
// carries the ACR signal for external consumers, and called the prefix "the internal UI's prefix".
// This is that internal UI, so it adds the prefix back, matching PaymentExtensions'
// BuildAccountCreditLabel — which is what the Payments card beside this modal renders.
const ACCOUNT_CREDIT_PREFIX = '[ACR]';

// Status arrives as the raw server enum member name — PaymentHistoryHandler stringifies
// AuthviaPaymentRequestStatus/DirectChargeStatus, and payments-sourced rows carry the literal
// "Completed". Every member of both enums is a single word except AwaitingCustomer (the
// sent-but-unpaid request, the most common row this feed shows), which would otherwise render
// unspaced beside the spaced Type labels. Unmapped values fall through unchanged, so a new
// single-word status needs no entry here — only a new multi-word one does.
const STATUS_LABELS: Record<string, string> = {
  AwaitingCustomer: 'Awaiting Customer',
};

@Component({
  selector: 'app-payment-history-modal',
  templateUrl: './payment-history-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GroundworksTableComponent,
    ModalBodyComponent,
    ModalComponent,
    ModalFooterComponent,
    ModalHeaderComponent,
    StateMessageComponent,
  ],
})
export class PaymentHistoryModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly paymentService = inject(PaymentService);
  private readonly destroyRef = inject(DestroyRef);

  readonly opportunityId = signal('');
  readonly entries = signal<PaymentHistoryEntry[]>([]);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly isForbidden = signal(false);

  // Order is Date, Type, Status, Amount — fixed by the ticket's "expected columns" scenario.
  // `entries()` is rendered in the order the endpoint returned it (newest first); do NOT sort here —
  // that ordering is the server handler's guarantee, covered by its own tests.
  readonly tableData = computed<GroundworksTableData>(() => ({
    // Widths are percentages, not px, so the split holds at any modal width. Type takes the
    // largest share because it carries by far the longest content — "[ACR] Financial Adjustment
    // for Customer Satisfaction" — while Date and Amount are fixed-width by nature. Left to auto
    // layout the browser gives Date nearly as much room as Type and the type name wraps.
    className: 'table-bordered',
    columns: [
      { label: 'Date', width: '15%' }, // NOT dataType 'date' — TableCell.value is string | number, see toDateCell().
      { label: 'Type' },
      { label: 'Status', width: '18%', align: 'center' }, // fits the longest status, "Awaiting Customer".
      { label: 'Amount', width: '15%', dataType: 'currency', align: 'end' },
    ],
    rows: this.entries().map((e) => ({
      type: 'data' as const,
      cells: [
        { value: this.toDateCell(e) },
        { value: this.toTypeCell(e) },
        { value: this.toStatusCell(e) },
        { value: e.amount },
      ],
    })),
  }));

  readonly retryButton = { text: 'Try Again', onClick: () => this.load() };

  readonly footerActions: ModalFooterAction[] = [
    { label: 'Close', type: 'outline-secondary', action: () => this.closeModal() },
  ];

  constructor() {
    effect(() => {
      const id = this.opportunityId();
      if (id) {
        this.load();
      }
    });
  }

  closeModal(): void {
    this.activeModal.close();
  }

  private load(): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.isForbidden.set(false);

    this.paymentService
      .getPaymentHistory(this.opportunityId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entries) => {
          this.entries.set(entries);
          this.isLoading.set(false);
        },
        error: (err: unknown) => {
          this.isLoading.set(false);
          if (this.isAuthFailure(err)) {
            this.isForbidden.set(true);
          } else {
            this.hasError.set(true);
          }
        },
      });
  }

  // occurredAt carries two shapes off the wire: true instants for requests/charges
  // (e.g. "2026-08-21T01:00:00Z") and date-only values for recorded payments/credits
  // (e.g. "2026-08-14T00:00:00", no `Z`). ECMAScript parses the offset-less form as
  // local midnight, so local formatting (no timezone arg) returns the same calendar
  // day in every timezone for that shape — and shows the correct local day for
  // instants too (a 9pm ET request would otherwise render as the next day in UTC).
  // The absent `Z` on the date-only shape is load-bearing: if the server ever starts
  // emitting those as UTC-midnight *with* a `Z`, local rendering would silently shift
  // them back a day for every US user.
  private toDateCell(entry: PaymentHistoryEntry): string {
    return formatDate(entry.occurredAt, 'MM/dd/yyyy', 'en-US');
  }

  // The server resolves the real method for every row whose payment it can identify — "Financed",
  // "Check", "Authvia ACH" — using the same call the Payments card behind this modal uses, so the
  // two panels agree. KIND_LABELS is only the fallback: a request still awaiting the customer, a
  // declined charge, or a capture that could not be identified unambiguously.
  // `||` not `??`, deliberately: a blank-but-present name must fall through to the kind label.
  // Account credits branch first and mirror BuildAccountCreditLabel exactly — "[ACR] <name>", or a
  // bare "[ACR]" when no name resolved. They never fall through to the kind label.
  private toTypeCell(entry: PaymentHistoryEntry): string {
    const name = entry.paymentMethodName?.trim();
    if (entry.kind === PaymentHistoryEntryKind.AccountCredit) {
      return name ? `${ACCOUNT_CREDIT_PREFIX} ${name}` : ACCOUNT_CREDIT_PREFIX;
    }
    return name || KIND_LABELS[entry.kind] || '—';
  }

  private toStatusCell(entry: PaymentHistoryEntry): string {
    if (!entry.status) return '—';
    return STATUS_LABELS[entry.status] ?? entry.status;
  }

  // SwaggerException.isSwaggerException reads a property off its argument and throws on
  // null/undefined, so the null check must come first.
  private isAuthFailure(err: unknown): boolean {
    if (err == null || !SwaggerException.isSwaggerException(err)) return false;
    return err.status === 401 || err.status === 403;
  }
}
